import {
  formatRafScore,
  RAF_COMPARISON_MONTHS,
  type RafScore,
} from '@seen/request-types/rafScore'
import {
  cn,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Separator,
} from '@seen/ui'
import {
  IconArrowNarrowRight,
  IconGauge,
  IconTrendingDown,
  IconTrendingUp,
} from '@tabler/icons-react'
import { format, parseISO } from 'date-fns'
import type { JSX, ReactNode } from 'react'
import { Link } from '#src/components/core/Link.tsx'
import { RISK_CONTRIBUTORS_ANCHOR } from '#src/pages/participants/conditions/riskContributorsAnchor.ts'

const TREND_ICONS = {
  up: IconTrendingUp,
  down: IconTrendingDown,
  flat: IconArrowNarrowRight,
} as const

const COMPARISON_LABEL = `${RAF_COMPARISON_MONTHS} months ago`

const TREND_LABELS = {
  up: `Up from ${COMPARISON_LABEL}`,
  down: `Down from ${COMPARISON_LABEL}`,
  flat: `Unchanged from ${COMPARISON_LABEL}`,
} as const

const TREND_DESCRIPTIONS = {
  up: 'Increasing',
  down: 'Decreasing',
  flat: 'Stable',
} as const

export function ParticipantRafScoreRow({
  rafScore,
  patientId,
  className,
  iconClassName,
}: {
  rafScore: RafScore
  // Absent on the presentation-only card, which sits inside the narrow-screen
  // sheet trigger and carries no links.
  patientId?: string
  className?: string
  iconClassName?: string
}): JSX.Element {
  const TrendIcon = rafScore.trend ? TREND_ICONS[rafScore.trend] : null
  const asOfDate = parseISO(rafScore.asOfMonth)
  const asOf = format(asOfDate, 'MMMM yyyy')
  const collectionYear = format(asOfDate, 'yyyy')

  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        <span className={cn('flex gap-1 leading-4', className)}>
          <IconGauge
            className={cn('size-3 shrink-0 self-center', iconClassName, 'mt-0')}
          />
          <span className="flex min-w-0 items-baseline gap-1">
            <span>RAF</span>
            <span className="font-medium text-foreground tabular-nums">
              {formatRafScore(rafScore.score)}
            </span>
            {TrendIcon && rafScore.trend && (
              <TrendIcon
                className="size-3 shrink-0 self-center"
                // An <svg> carrying only aria-label has no accessible name in
                // most screen readers; the img role is what gets it announced.
                role="img"
                aria-label={TREND_LABELS[rafScore.trend]}
              />
            )}
          </span>
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side="bottom"
        align="start"
        className="flex w-80 flex-col gap-2"
      >
        <h3 className="text-base leading-6 font-semibold tracking-[-0.53px] text-foreground">
          Internal Risk Score
        </h3>
        <p className="text-xs leading-4 text-foreground-secondary">
          Seen Health's internal risk score based on documented {collectionYear}{' '}
          diagnoses from Seen Health PCPs and external providers. Resets each
          January and builds throughout the year as conditions are captured.
        </p>
        <Separator />
        {rafScore.trend && (
          <RafScoreDetailRow label="Trend">
            {TREND_DESCRIPTIONS[rafScore.trend]}
          </RafScoreDetailRow>
        )}
        <RafScoreDetailRow label={`As of ${asOf}`}>
          {formatRafScore(rafScore.score)}
        </RafScoreDetailRow>
        {rafScore.previousScore !== null && (
          <RafScoreDetailRow label={COMPARISON_LABEL}>
            {formatRafScore(rafScore.previousScore)}
          </RafScoreDetailRow>
        )}
        {patientId && (
          <>
            <Separator />
            <Link
              to={`/Patient/${patientId}/conditions#${RISK_CONTRIBUTORS_ANCHOR}`}
              className="text-xs leading-4 text-foreground-secondary hover:text-foreground"
            >
              See breakdown
            </Link>
          </>
        )}
      </HoverCardContent>
    </HoverCard>
  )
}

function RafScoreDetailRow({
  label,
  children,
}: {
  label: string
  children: ReactNode
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2 text-xs leading-4">
      <span className="text-foreground">{label}</span>
      <span className="text-foreground-secondary tabular-nums">{children}</span>
    </div>
  )
}
